import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { clearRecentRoutes, loadRecentRoutes, RecentRouteEntry } from '../utils/recent';
import { C, FONT, R, SP } from '../theme/tokens';

function friendlyTitleFromPath(path: string[]): string {
  return path[path.length - 1] ?? 'Unknown';
}

export default function RecentCalculationsScreen({ navigation }: any) {
  const [items, setItems] = useState<RecentRouteEntry[]>([]);
  const [query, setQuery] = useState('');

  const refresh = useCallback(async () => {
    setItems(await loadRecentRoutes());
  }, []);

  useEffect(() => {
    const unsub = navigation.addListener('focus', refresh);
    return unsub;
  }, [navigation, refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((e) => {
      const title = (e.title ?? friendlyTitleFromPath(e.path)).toLowerCase();
      return title.includes(q) || e.path.join(' > ').toLowerCase().includes(q);
    });
  }, [items, query]);

  const handleOpen = (entry: RecentRouteEntry) => {
    if (!entry.path.length) return;
    if (entry.path.length === 1) {
      navigation.navigate(entry.path[0]);
      return;
    }
    navigation.navigate(entry.path[0], { screen: entry.path[1] });
  };

  const doClear = async () => {
    await clearRecentRoutes();
    await refresh();
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.eyebrowRow}>
          <View style={styles.eyebrowBar} />
          <Text style={styles.eyebrowText}>HISTORY</Text>
        </View>
        <Text style={styles.title}>Recent Calculations</Text>
        <Text style={styles.subtitle}>
          Quickly jump back to calculators you used recently.
        </Text>
      </View>

      {/* Search */}
      <View style={styles.searchBox}>
        <Ionicons name="search-outline" size={16} color={C.dim} style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search calculators…"
          placeholderTextColor={C.dim}
          value={query}
          onChangeText={setQuery}
          clearButtonMode="while-editing"
        />
      </View>

      {/* Actions */}
      <View style={styles.actions}>
        <Pressable style={({ pressed }) => [styles.actionBtn, pressed && styles.pressed]} onPress={refresh}>
          <Ionicons name="refresh-outline" size={15} color={C.ink2} />
          <Text style={styles.actionText}>Refresh</Text>
        </Pressable>
        <Pressable style={({ pressed }) => [styles.actionBtn, pressed && styles.pressed]} onPress={doClear}>
          <Ionicons name="trash-outline" size={15} color={C.ink2} />
          <Text style={styles.actionText}>Clear all</Text>
        </Pressable>
      </View>

      {/* Empty state */}
      {!filtered.length ? (
        <View style={styles.emptyCard}>
          <Ionicons name="time-outline" size={28} color={C.dim} style={{ marginBottom: SP.sm }} />
          <Text style={styles.emptyText}>
            No recent calculators yet.{'\n'}Open any calculator and it will appear here.
          </Text>
        </View>
      ) : (
        filtered.map((entry, idx) => {
          const title = entry.title ?? friendlyTitleFromPath(entry.path);
          const pathLabel = entry.path.join(' › ');
          const when = new Date(entry.ts).toLocaleString();

          return (
            <Pressable
              key={`${entry.path.join('>')}-${idx}`}
              style={({ pressed }) => [styles.card, pressed && styles.pressed]}
              onPress={() => handleOpen(entry)}
            >
              <View style={styles.cardBody}>
                <Text style={styles.cardTitle} numberOfLines={1}>{title}</Text>
                <Text style={styles.cardPath} numberOfLines={1}>{pathLabel}</Text>
                <Text style={styles.cardWhen}>{when}</Text>
              </View>
              <Text style={styles.cardChevron}>›</Text>
            </Pressable>
          );
        })
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: C.bg,
  },
  content: {
    padding: SP.md,
    paddingBottom: SP.xl + SP.md,
    width: '100%',
    maxWidth: 980,
    alignSelf: 'center',
  },

  header: { marginBottom: SP.md },
  eyebrowRow: { flexDirection: 'row', alignItems: 'center', marginBottom: SP.xs },
  eyebrowBar: {
    width: 4, height: 11,
    backgroundColor: C.accent,
    borderRadius: 1,
    marginRight: SP.sm,
  },
  eyebrowText: {
    fontFamily: FONT.mono,
    fontSize: 11,
    color: C.dim,
    letterSpacing: 0.8,
  },
  title: {
    fontFamily: FONT.sansBold,
    fontSize: 28,
    color: C.ink,
    letterSpacing: -0.4,
  },
  subtitle: {
    fontFamily: FONT.sans,
    fontSize: 14,
    color: C.ink2,
    marginTop: SP.xs + 2,
    lineHeight: 20,
  },

  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: C.panel,
    borderRadius: R.field,
    borderWidth: 1,
    borderColor: C.edge,
    paddingHorizontal: SP.sm + 4,
    paddingVertical: SP.sm + 2,
    marginBottom: SP.sm,
  },
  searchIcon: { marginRight: SP.sm },
  searchInput: {
    flex: 1,
    fontFamily: FONT.sans,
    fontSize: 15,
    color: C.ink,
  },

  actions: {
    flexDirection: 'row',
    gap: SP.sm,
    marginBottom: SP.md,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SP.xs + 2,
    backgroundColor: C.panel,
    borderRadius: R.button,
    borderWidth: 1,
    borderColor: C.edge,
    paddingVertical: SP.sm,
    paddingHorizontal: SP.sm + 4,
  },
  actionText: {
    fontFamily: FONT.sansMed,
    fontSize: 13,
    color: C.ink2,
  },

  emptyCard: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    padding: SP.lg,
    alignItems: 'center',
    marginTop: SP.md,
  },
  emptyText: {
    fontFamily: FONT.sans,
    fontSize: 14,
    color: C.dim,
    textAlign: 'center',
    lineHeight: 20,
  },

  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    padding: SP.md,
    marginBottom: SP.sm + 2,
  },
  pressed: { opacity: 0.82 },
  cardBody: { flex: 1 },
  cardTitle: {
    fontFamily: FONT.sansMed,
    fontSize: 15,
    color: C.ink,
    marginBottom: 3,
  },
  cardPath: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.ink2,
    marginBottom: 4,
  },
  cardWhen: {
    fontFamily: FONT.mono,
    fontSize: 11,
    color: C.dim,
  },
  cardChevron: {
    fontFamily: FONT.mono,
    fontSize: 22,
    color: C.dim,
    marginLeft: SP.sm,
  },
});
